import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck,
  Phone,
  MessageSquareText,
  Clock,
  Scissors,
  UserRound,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import TraitChip from './TraitChip';
import ConsentBadge from './ConsentBadge';
import type { CustomerDetail, TimelineEntry } from '../../services/customerService';

function formatDate(ts: string | null): string {
  if (!ts) return '–';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Stat({
  icon: Icon,
  value,
  label,
}: {
  icon: typeof CalendarCheck;
  value: string | number;
  label: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-background/60 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        <span className="text-[11px] uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

const KIND_ICON = {
  booking: CalendarCheck,
  call: Phone,
  whatsapp: MessageSquareText,
} as const;

export default function CustomerProfilePane({
  customer,
  timeline,
}: {
  customer: CustomerDetail;
  timeline: TimelineEntry[];
}) {
  const navigate = useNavigate();
  const title = customer.name?.trim() || (customer.phone ? `+${customer.phone}` : 'Unbekannt');

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-xl font-semibold text-foreground">{title}</h2>
            {customer.phone && (
              <p className="mt-0.5 font-mono text-sm text-muted-foreground">+{customer.phone}</p>
            )}
            {customer.email && (
              <p className="truncate text-sm text-muted-foreground">{customer.email}</p>
            )}
          </div>
          <ConsentBadge status={customer.consent_status} className="mt-1 flex-shrink-0" />
        </div>

        {customer.traits.length > 0 && (
          <div className="mt-3">
            <div className="flex flex-wrap gap-1.5">
              {customer.traits.map((trait) => (
                <TraitChip key={trait.trait} trait={trait} />
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Aus gebuchten Leistungen abgeleitet – keine Angaben des Kunden.
            </p>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1 pt-4">
        {/* Numbers */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat icon={CalendarCheck} value={customer.bookings_total} label="Buchungen" />
          <Stat icon={Phone} value={customer.calls_total} label="Anrufe" />
          <Stat icon={MessageSquareText} value={customer.whatsapp_sessions_total} label="WhatsApp" />
          <Stat
            icon={Clock}
            value={
              customer.avg_days_between_bookings != null
                ? `${Math.round(customer.avg_days_between_bookings)} T`
                : '–'
            }
            label="Rhythmus"
          />
        </div>

        {/* Preferences — only what we actually know */}
        {(customer.preferred_employee || customer.preferred_daypart || customer.channel_preference) && (
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 rounded-lg bg-muted/40 px-3 py-2.5 text-sm">
            {customer.preferred_employee && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <UserRound className="h-3.5 w-3.5" aria-hidden />
                Meist bei <span className="font-medium text-foreground">{customer.preferred_employee}</span>
              </span>
            )}
            {customer.preferred_daypart && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Clock className="h-3.5 w-3.5" aria-hidden />
                Meist <span className="font-medium text-foreground">{customer.preferred_daypart}</span>
              </span>
            )}
            {customer.channel_preference && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                {customer.channel_preference === 'voice' ? (
                  <Phone className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <MessageSquareText className="h-3.5 w-3.5" aria-hidden />
                )}
                Bucht meist per{' '}
                <span className="font-medium text-foreground">
                  {customer.channel_preference === 'voice' ? 'Telefon' : 'WhatsApp'}
                </span>
              </span>
            )}
          </div>
        )}

        {/* Most-booked services */}
        {customer.top_items.length > 0 && (
          <section>
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <Scissors className="h-4 w-4 text-muted-foreground" aria-hidden />
              Meistgebuchte Leistungen
            </h3>
            <ul className="space-y-1">
              {customer.top_items.map((item) => (
                <li
                  key={item.item_no}
                  className="flex items-center justify-between rounded-md border border-border/60 px-3 py-1.5 text-sm"
                >
                  <span className="truncate text-foreground">{item.name || `Leistung ${item.item_no}`}</span>
                  <span className="ml-3 flex-shrink-0 tabular-nums text-muted-foreground">
                    {item.count}×
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Timeline */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-foreground">Verlauf</h3>
          {timeline.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Noch keine Termine, Anrufe oder Nachrichten
            </p>
          ) : (
            <ul className="space-y-1">
              {timeline.map((entry, index) => {
                const Icon = KIND_ICON[entry.kind] ?? CalendarCheck;
                const clickable = entry.conversation_id != null;
                return (
                  <li key={`${entry.kind}-${index}`}>
                    <div
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      onClick={
                        clickable
                          ? () => navigate(`/conversations?c=${entry.conversation_id}`)
                          : undefined
                      }
                      onKeyDown={
                        clickable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(`/conversations?c=${entry.conversation_id}`);
                              }
                            }
                          : undefined
                      }
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm',
                        clickable
                          ? 'cursor-pointer hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                          : '',
                      )}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-foreground">{entry.title}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {formatDate(entry.at)}
                          {entry.detail ? ` · ${entry.detail}` : ''}
                        </div>
                      </div>
                      {clickable && (
                        <span className="flex flex-shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
                          Verlauf
                          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
